import React, { useState, useRef, useEffect, useContext } from 'react';
import { Row, Col, Form, Button, Toast, ToastContainer, Dropdown } from 'react-bootstrap';
import { chatApi } from '../services/api';
import useSettings from '../hooks/useSettings';
import WelcomeComponent from './WelcomeComponent';
import ReactMarkdown from 'react-markdown';
import './ChatInterface.css';
import './iframe-styles.css';

// Custom link renderer for ReactMarkdown - opens links in new tab
const markdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="markdown-link">
      {children}
      <i className="bi bi-box-arrow-up-right external-link-icon"></i>
    </a>
  )
};

const ChatInterface = () => {
  const MAX_MESSAGE_LENGTH = 2000;
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatStarters, setChatStarters] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [activeToolCall, setActiveToolCall] = useState(null);
  const abortStreamRef = useRef(null);
  
  // Reference to the input field for focusing
  const inputRef = useRef(null);
  
  // Detect if running in an iframe and apply appropriate class
  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (isInIframe) {
      document.documentElement.classList.add('in-iframe');
    }
    
    return () => {
      document.documentElement.classList.remove('in-iframe');
    };
  }, []);

  // Use settings hook to get chat starters
  const { getSetting, isLoading: settingsLoading, isLoaded } = useSettings();
  
  // Update chat starters when settings are loaded
  useEffect(() => {
    // Only update when settings are loaded
    if (isLoaded) {
      // Get chat starters from settings
      const startersString = getSetting('chat_starters', '');
      const startersArray = startersString ? startersString.split(';').filter(Boolean) : [];
      setChatStarters(startersArray);
    }
    
    // Update loading state
    // setLoadingStarters(settingsLoading);
  }, [isLoaded]); // Only depend on isLoaded to prevent loops

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    if (inputMessage.length > MAX_MESSAGE_LENGTH) {
      setToastMessage(`Message exceeds maximum length of ${MAX_MESSAGE_LENGTH} characters`);
      setShowToast(true);
      return;
    }
    
    // Add user message to chat
    const userMessage = { role: 'user', content: inputMessage };
    const currentMessage = inputMessage.trim();
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsStreaming(true);
    setStreamingText('');
    setActiveToolCall(null);
    
    let accumulatedText = '';
    let streamCitations = [];
    
    // Try streaming first, fallback to non-streaming on error
    try {
      abortStreamRef.current = chatApi.sendMessageStream(
        currentMessage,
        conversationId,
        {
          onConversationId: (id) => {
            setConversationId(id);
          },
          onText: (text) => {
            accumulatedText += text;
            setStreamingText(accumulatedText);
            setActiveToolCall(null); // Clear tool call indicator when text starts
          },
          onToolCallStarted: (name) => {
            setActiveToolCall(name);
          },
          onToolResult: (name, citations) => {
            if (citations && citations.length > 0) {
              streamCitations = [...streamCitations, ...citations];
            }
            setActiveToolCall(null);
          },
          onGenerating: () => {
            setActiveToolCall(null);
          },
          onDone: (citations) => {
            // Merge any final citations
            if (citations && citations.length > 0) {
              streamCitations = [...streamCitations, ...citations];
            }
            
            // Add the complete assistant message
            setMessages(prevMessages => [
              ...prevMessages,
              {
                role: 'assistant',
                content: accumulatedText,
                hasCitations: streamCitations.length > 0,
                citations: streamCitations
              }
            ]);
            
            setIsLoading(false);
            setIsStreaming(false);
            setStreamingText('');
            setActiveToolCall(null);
            abortStreamRef.current = null;
          },
          onError: async (error) => {
            console.error('Streaming error, falling back to non-streaming:', error);
            
            // Fallback to non-streaming
            try {
              const result = await chatApi.sendMessage(currentMessage, conversationId);
              
              if (result.conversation_id) {
                setConversationId(result.conversation_id);
              }
              
              setMessages(prevMessages => [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: result.text,
                  hasCitations: result.citations && result.citations.length > 0,
                  citations: result.citations || []
                }
              ]);
            } catch (fallbackError) {
              console.error('Fallback also failed:', fallbackError);
              setMessages(prevMessages => [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: 'Sorry, I encountered an error. Please try again later.',
                  citations: []
                }
              ]);
            }
            
            setIsLoading(false);
            setIsStreaming(false);
            setStreamingText('');
            setActiveToolCall(null);
            abortStreamRef.current = null;
          }
        }
      );
    } catch (error) {
      console.error('Error initiating stream:', error);
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingText('');
      setActiveToolCall(null);
    }
  };

  const handleStarterClick = (starter) => {
    setInputMessage(starter);
    setShowWelcome(false);
    // Submit the form after a short delay to allow state to update
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, 100);
  };
  
  const handleCustomPrompt = () => {
    setShowWelcome(false);
    // Focus on the input field
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
  };
  
  // Handle exporting a message with specified format
  const handleExportMessage = async (message, index, format = 'pdf') => {
    try {
      setExportLoading(true);
      
      // Generate a unique message ID if not available
      const messageId = `msg-${Date.now()}-${index}`;
      
      // Update the API service to include the message content, citations, and format
      const result = await chatApi.exportMessageAsPdf(
        message.content,
        messageId,
        conversationId,
        message.citations || [],
        format
      );
      
      // Show success toast with appropriate format
      const formatName = format.toUpperCase();
      setToastMessage(`Message exported as ${formatName} successfully!`);
      setShowToast(true);
    } catch (error) {
      console.error(`Error exporting message as ${format}:`, error);
      setToastMessage(`Failed to export message as ${format}. Please try again.`);
      setShowToast(true);
    } finally {
      setExportLoading(false);
    }
  };
  
  // Handle copying message content to clipboard with citations
  const handleCopyMessage = async (message) => {
    try {
      
      // Start with the message content
      let textToCopy = message.content;
      
      // Add citations if they exist
      if (message.hasCitations && message.citations && message.citations.length > 0) {
        textToCopy += '\n\n## Citations\n';
        message.citations.forEach((citation, index) => {
          textToCopy += `\n${index + 1}. ${citation.filename}`;
          if (citation.url && citation.type === 'knowledge_base') {
            textToCopy += ` - ${citation.url}`;
          }
        });
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setToastMessage('Message with citations copied to clipboard!');
      setShowToast(true);
    } catch (error) {
      console.error('Error copying message:', error);
      setToastMessage('Failed to copy message. Please try again.');
      setShowToast(true);
    }
  };
  

  return (
    <div className="chat-container">
      <Row className="chat-window">
        <Col>
          <div className="messages-container">
              {messages.length === 0 && showWelcome ? (
                <WelcomeComponent 
                  chatStarters={chatStarters} 
                  onStarterClick={handleStarterClick}
                  onCustomPrompt={handleCustomPrompt}
                />
              ) : messages.length > 0 ? (
                messages.map((message, index) => (
                  <div 
                    key={index} 
                    className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                  >
                    <div className="message-content">
                      {message.role === 'assistant' && (
                        <div className="assistant-avatar">
                          <img src="img/karl-b-gpt.png" alt="Karl B" className="avatar-image" />
                        </div>
                      )}
                      {message.role === 'assistant' && (
                        <div className="message-actions">
                          <Button 
                            className="export-btn me-2" 
                            onClick={() => handleCopyMessage(message)}
                          >
                            <i className="bi bi-clipboard"></i>
                            Copy
                          </Button>
                          <Dropdown>
                            <Dropdown.Toggle 
                              className="export-btn" 
                              disabled={exportLoading}
                              id={`export-dropdown-${index}`}
                            >
                              <i className="bi bi-file-earmark"></i>
                              {exportLoading ? 'Exporting...' : 'Export'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item 
                                onClick={() => handleExportMessage(message, index, 'md')}
                                disabled={exportLoading}
                              >
                                <i className="bi bi-file-earmark-text me-2"></i>
                                Markdown Document
                              </Dropdown.Item>
                              <Dropdown.Item 
                                onClick={() => handleExportMessage(message, index, 'docx')}
                                disabled={exportLoading}
                              >
                                <i className="bi bi-file-earmark-word me-2"></i>
                                Word Document
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      )}
                      {message.role === 'assistant' ? (
                        <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
                      ) : (
                        message.content
                      )}
                      {message.hasCitations && message.citations && message.citations.length > 0 && (
                        <div className="citations-container">
                          <h6>Citations:</h6>
                          <ul className="list-group">
                            {message.citations.map((citation, citIndex) => (
                              <li key={citIndex} className="list-group-item d-flex justify-content-between align-items-center">
                                <a className="citation-link" href={citation.url} target="_blank" rel="noopener noreferrer">
                                  {citation.type === 'knowledge_base' ? (
                                    <i className="bi bi-journal-text"></i>
                                  ) : (
                                    <i className="bi bi-globe"></i>
                                  )}
                                  <span className="ms-2">{citation.filename}</span>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : null}
              {isLoading && (
                <div className="message assistant-message">
                  <div className="message-content">
                    <div className="assistant-avatar">
                      <img src="img/karl-b-gpt.png" alt="Karl B" className="avatar-image" />
                    </div>
                    {isStreaming && streamingText ? (
                      <>
                        <ReactMarkdown components={markdownComponents}>{streamingText}</ReactMarkdown>
                        <span className="streaming-cursor">▌</span>
                      </>
                    ) : activeToolCall ? (
                      <div className="tool-call-indicator">
                        <i className="bi bi-gear-fill spinning"></i>
                        <span className="ms-2">
                          {activeToolCall === 'search_knowledge_base' && 'Searching knowledge base...'}
                          {activeToolCall === 'search_web' && 'Searching the web...'}
                          {activeToolCall !== 'search_knowledge_base' && activeToolCall !== 'search_web' && `Running ${activeToolCall}...`}
                        </span>
                      </div>
                    ) : (
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </Col>
      </Row>
      
      
      <div className="input-row">
          <Form onSubmit={handleSubmit}>
            <div className="input-group">
              <Form.Control
                ref={inputRef}
                as="textarea"
                rows={1}
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                maxLength={MAX_MESSAGE_LENGTH}
                disabled={isLoading}
                style={{ resize: 'none', overflowY: 'auto', maxHeight: '120px' }}
              />
              <Button 
                variant="primary" 
                type="submit" 
                disabled={!inputMessage.trim() || isLoading || inputMessage.length > MAX_MESSAGE_LENGTH}
              >
                <i className="bi bi-send"></i>
              </Button>
            </div>
            <div className="character-counter">
              <small className={inputMessage.length > MAX_MESSAGE_LENGTH * 0.9 ? 'text-warning' : 'text-muted'}>
                {inputMessage.length} / {MAX_MESSAGE_LENGTH} characters
              </small>
            </div>
          </Form>
      </div>

      {/* Toast notification */}
      <ToastContainer position="bottom-center" className="p-3">
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={3000} 
          autohide
          bg="light"
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ChatInterface;
